import compact from 'lodash/compact';
import countBy from 'lodash/countBy';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
// The default export of http-status-codes@2 omits FAILED_DEPENDENCY; the named map has it.
import { StatusCodes } from 'http-status-codes';
import type { PlatformInsightSeverity, PlatformSyncIntegrationExpandedV2 } from '@cloudquery/api-platform-multi-tenant';
import type { CloudQueryConnectStateRead, CloudQueryProvisioningStep, CloudQuerySetupState } from 'types/api.types';
import type { EnvironmentApi } from '@env0/environment-service/api';

export const SEVERITY_ORDER: PlatformInsightSeverity[] = ['Critical', 'High', 'Medium', 'Low'];

// Built-in insight sources start with the vendor name, which env zero's UI must not show (ATL-624).
export const toInsightSourceLabel = (source?: string) => source?.replace(/^CloudQuery\s+/i, '');

// Evidence and mitigation can repeat the insight's own source name; show it the way the source label does.
export const relabelInsightSource = (text: string | undefined, source: string | undefined) =>
  text && source ? text.split(source).join(toInsightSourceLabel(source) ?? source) : text;

export const INSIGHTS_ERROR_TITLE_ID = 'cloudquery.insights.error';

/* The statuses the CloudQuery proxy distinguishes (services/cloudquery-service/functions/proxy.ts):
   403 when the caller lacks VIEW_DASHBOARD, 409 when the organization has no connection, 424 when
   CloudQuery rejected the stored credentials. Anything else is a genuine failure worth retrying. */
const INSIGHTS_ERROR_TITLE_IDS: Record<number, string> = {
  [StatusCodes.FORBIDDEN]: `${INSIGHTS_ERROR_TITLE_ID}.forbidden`,
  [StatusCodes.CONFLICT]: `${INSIGHTS_ERROR_TITLE_ID}.disconnected`,
  [StatusCodes.FAILED_DEPENDENCY]: `${INSIGHTS_ERROR_TITLE_ID}.credentials`
};

export const insightsErrorTitleId = (status?: number): string =>
  (status !== undefined && INSIGHTS_ERROR_TITLE_IDS[status]) || INSIGHTS_ERROR_TITLE_ID;

export const sqlString = (value: string): string => `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

export const sqlStringList = (values: string[]): string => uniq(values).sort().map(sqlString).join(', ');

export const buildAssetInventoryUrl = (tenantUrl: string, assetId: string): string => {
  const params = new URLSearchParams({
    groupBy: '_cq_app_id',
    detailIds: assetId,
    'relatedResourceId-tab': 'insights'
  });

  return `${tenantUrl}/asset-inventory?${params}`;
};

export const buildInsightUrl = (tenantUrl: string, insightId: string): string => `${tenantUrl}/insights/${insightId}`;

// The tenant host answers 200 for any path, so a bad route only shows as the SPA's not-found page.
// Route owned by cloudquery/frontend (apps/platform-multi-tenant/src/routeTree.gen.ts).
export const buildAddProviderUrl = (tenantUrl: string): string => `${tenantUrl}/data-pipelines/integrations/create`;

const modulePrefix = (moduleName?: string): string =>
  moduleName
    ? moduleName
        .split('.')
        .map(module => `module.${module}.`)
        .join('')
    : '';

export const toResourceAddress = ({
  provider,
  type,
  name,
  moduleName,
  mode
}: EnvironmentApi.EnvironmentResource): string =>
  `${modulePrefix(moduleName)}${mode === 'data' ? 'data.' : ''}${provider}_${type}.${name}`;

export type InsightResourceRow = {
  _cq_platform_id?: string;
  _cq_insight_id?: string;
  severity?: PlatformInsightSeverity;
  title?: string;
  source?: string;
  source_category?: string;
  insight_category?: string;
  account?: string;
  region?: string;
  cloud?: string;
  resource_type?: string;
};

export type CloudQueryInsight = {
  id: string;
  title: string;
  severity: PlatformInsightSeverity;
  source: string;
  sourceCategory: string;
  insightCategory: string;
  platformIds: string[];
};

export type CloudQueryInsightResource = {
  platformId: string;
  name: string;
  type?: string;
  account?: string;
  cloud?: string;
  region?: string;
};

export type InsightSection = { insight: CloudQueryInsight; resources: CloudQueryInsightResource[] };
export type InsightsByResourceAddress = Map<string, CloudQueryInsight[]>;

// Environment insight titles are authored fleet-wide ("env zero Environments Without an Approval Gate"),
// so the fleet prefix comes off when one is shown against a single environment.
export const toEnvironmentInsightTitle = (title: string): string => title.replace(/^env zero (Environments )?/, '');

export const toCloudQueryInsights = (rows: InsightResourceRow[]): CloudQueryInsight[] => {
  const byId = new Map<string, CloudQueryInsight>();

  for (const row of rows) {
    if (!row._cq_insight_id || !row.severity) continue;

    const insight = byId.get(row._cq_insight_id);

    if (insight) {
      insight.platformIds.push(...compact([row._cq_platform_id]));
      continue;
    }

    byId.set(row._cq_insight_id, {
      id: row._cq_insight_id,
      title: row.title ?? '',
      severity: row.severity,
      source: row.source ?? '',
      sourceCategory: row.source_category ?? '',
      insightCategory: row.insight_category ?? '',
      platformIds: compact([row._cq_platform_id])
    });
  }

  return sortBy(
    [...byId.values()].map(insight => ({ ...insight, platformIds: uniq(insight.platformIds) })),
    ({ severity }) => SEVERITY_ORDER.indexOf(severity)
  );
};

export const toInsightResources = (
  rows: InsightResourceRow[],
  addressByPlatformId: Map<string, string>
): Map<string, CloudQueryInsightResource> => {
  const resources = new Map<string, CloudQueryInsightResource>();

  for (const row of rows) {
    const platformId = row._cq_platform_id;
    if (!platformId || resources.has(platformId)) continue;

    resources.set(platformId, {
      platformId,
      name: addressByPlatformId.get(platformId) ?? platformId,
      type: row.resource_type,
      account: row.account,
      cloud: row.cloud,
      region: row.region
    });
  }

  return resources;
};

export const splitInsightsByDrift = (
  insights: CloudQueryInsight[],
  resourceByPlatformId: Map<string, CloudQueryInsightResource>,
  driftedPlatformIds: Set<string>
): { drifted: InsightSection[]; notDrifted: InsightSection[] } => {
  const drifted: InsightSection[] = [];
  const notDrifted: InsightSection[] = [];

  for (const insight of insights) {
    const resources = compact(insight.platformIds.map(platformId => resourceByPlatformId.get(platformId)));
    const [driftedResources, notDriftedResources] = partition(resources, ({ platformId }) =>
      driftedPlatformIds.has(platformId)
    );

    if (!isEmpty(driftedResources)) drifted.push({ insight, resources: driftedResources });
    if (!isEmpty(notDriftedResources)) notDrifted.push({ insight, resources: notDriftedResources });
  }

  return { drifted, notDrifted };
};

export const toSeverityCounts = (insights: CloudQueryInsight[]): Partial<Record<PlatformInsightSeverity, number>> =>
  countBy(insights, 'severity');

/* The four steps the progress screen shows, in provisioning order. `signup_abandoned` is deliberately
   not one of them: it is the handoff expiring before CloudQuery ever started, so it fails the first
   step rather than adding a fifth. */
export const CONTEXT_LAYER_STEPS = ['workspace', 'context_layer', 'envzero_integration', 'first_sync'] as const;

export type ContextLayerStep = (typeof CONTEXT_LAYER_STEPS)[number];
export type ContextLayerStepStatus = 'finish' | 'process' | 'error' | 'wait';

const failedStepIndex = (step?: CloudQueryProvisioningStep): number => {
  if (!step || step === 'signup_abandoned') return 0;

  const index = CONTEXT_LAYER_STEPS.indexOf(step as ContextLayerStep);

  return index === -1 ? 0 : index;
};

/* Provisioning `created` means the tenant exists and CloudQuery holds env zero's key - the first two
   steps - and only the callback that connects env zero is outstanding. */
const setupStepsInFlight = (state: CloudQuerySetupState): ContextLayerStepStatus[] => {
  const reached = state.provisioningState === 'created' ? 2 : 0;

  return CONTEXT_LAYER_STEPS.map((_step, index) => {
    if (index < reached) return 'finish';

    return index === reached ? 'process' : 'wait';
  });
};

/**
 * The legacy handoff reports no intermediate step - only a terminal `connected` or `failed` - so a run
 * in flight marks the first step in progress and leaves the rest waiting. Claiming more than that would
 * be an animation, not a status. A setup row (ATL-468) does report Cloud's provisioning axis, which
 * moves the marker along.
 */
export const toContextLayerStepStatuses = (state?: CloudQueryConnectStateRead): ContextLayerStepStatus[] => {
  if (state?.status === 'connected') return CONTEXT_LAYER_STEPS.map(() => 'finish');

  if (state?.status === 'pending' && state.mode === 'setup') return setupStepsInFlight(state);

  if (state?.status === 'failed' || state?.status === 'expired') {
    const failedAt = failedStepIndex(state.failureStep);

    return CONTEXT_LAYER_STEPS.map((_step, index) => {
      if (index < failedAt) return 'finish';

      return index === failedAt ? 'error' : 'wait';
    });
  }

  if (state?.status === 'pending') return CONTEXT_LAYER_STEPS.map((_step, index) => (index === 0 ? 'process' : 'wait'));

  return CONTEXT_LAYER_STEPS.map(() => 'wait');
};

export const isSetupStuck = (state?: CloudQueryConnectStateRead): boolean =>
  state?.mode === 'setup' && state.status === 'pending' && state.provisioningState === 'stuck';

export type CloudQueryProviderChip = { name: string; label: string; pluginName?: string };

/* The envzero source is the integration this connect flow creates itself. It is plumbing, not a cloud
   provider the user connected, so it never becomes a chip - otherwise a brand-new tenant looks like it
   already has one. */
const isEnvZeroSource = ({ plugin, source }: PlatformSyncIntegrationExpandedV2): boolean =>
  plugin?.name === 'envzero' || Boolean(source?.path?.endsWith('/envzero'));

export const toProviderChips = (integrations: PlatformSyncIntegrationExpandedV2[]): CloudQueryProviderChip[] => {
  const byName = new Map<string, CloudQueryProviderChip>();

  for (const integration of integrations) {
    if (isEnvZeroSource(integration)) continue;

    const name = integration.plugin?.name ?? integration.source?.path ?? integration.name;
    if (!name || byName.has(name)) continue;

    byName.set(name, { name, label: integration.plugin?.display_name ?? name, pluginName: integration.plugin?.name });
  }

  return sortBy([...byName.values()], ({ label }) => label.toLowerCase());
};
